import React from 'react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DesignIcon from '@mui/icons-material/DesignServices';
import ExportIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ListIcon from '@mui/icons-material/List';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ImportIcon from '@mui/icons-material/Upload';
import { Divider } from '@mui/material';

import { ReadFile, useHiddenFileSelect } from '@paragon/client-tools';
import {
  hasAutoLayoutFeatureFlag,
  hasCrowdbuildStitcherImportFeatureFlag,
  hasNewLayoutModeFeatureFlag,
  hasTrussDesignPermission,
  isLoggedInUserRoot,
} from '@paragon/login-tools';

import { useAppMonitor } from '../../../../monitor';
import {
  generateEnvelopesForRoofLayout,
  importCrowdBuildJson,
  importProjectDataFile,
} from '../../../../store/actions/layout/layoutActions';
import { designAllTrussEnvelopes } from '../../../../store/actions/layout/trussEnvelopeActions';
import { showChooseProjectDialog, showCreateEstimateDialog } from '../../../../store/actions/popupActions';
import { downloadProject } from '../../../../store/actions/projectActions';
import { handleStandardSceneItemEditPropertiesAction } from '../../../../store/actions/sceneInteractionActions';
import { getProjectLayoutModel } from '../../../../store/selectors/getProjectLayoutModel';
import { isLayoutModeViewOnly, isLayoutViewActive } from '../../../../store/selectors/layoutModeSelectors';
import { clearRightClickedItems } from '../../../../store/slices/viewportSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import CascadingSceneMenuItem from '../common/CascadingSceneMenuItem';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuFragment } from '../common/SceneMenuFragment';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { AddItemMenuItem } from '../shared-menu-items/AddItemMenuItem';
import { AnalyzeAllChangedMenuItem } from '../shared-menu-items/AnalyzeAllChangedMenuItem';
import { ImportComponentDesignMenuItem } from '../shared-menu-items/ImportComponentDesignMenuItem';
import { LockOptionsSubMenu } from '../shared-menu-items/LockOptionsSubMenu';
import { SettingsSubMenu } from '../shared-menu-items/SettingsSubMenu';

// IMPORTANT NOTE -- unlike most other context menus, a project menu can be open for a project that is not currently
//   selected. Ensure you select the project if appropriate when menu items are clicked.

export function ProjectMenu(props: { guid: string }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const project = useAppSelector(getProjectLayoutModel);

  const canDesign = useAppSelector(hasTrussDesignPermission);

  const isRootUser = useAppSelector(isLoggedInUserRoot);

  const inLayoutMode = useAppSelector(isLayoutViewActive);
  const newLayoutModeEnabled = useAppSelector(hasNewLayoutModeFeatureFlag);
  const layoutIsViewOnly = useAppSelector(isLayoutModeViewOnly);
  const autoLayoutEnabled = useAppSelector(hasAutoLayoutFeatureFlag);
  const canImportCrowdbuildFiles = useAppSelector(hasCrowdbuildStitcherImportFeatureFlag);

  const [renderFileSelect, selectFile] = useHiddenFileSelect('.json,.ppf', false);

  const processUploadedFile = async (file: ReadFile, { layoutOnly }: { layoutOnly: boolean } = { layoutOnly: false }) =>
    await dispatch(importProjectDataFile(file, { layoutOnly }));

  const processCrowdBuildFile = async (file: ReadFile) => {
    await dispatch(importCrowdBuildJson(atob(file.contents.split(',')[1])));
  };

  const requestQuoteItem = (
    <SceneMenuItem
      icon={<RequestQuoteIcon />}
      text="Create Estimate"
      onClick={() => {
        monitor.recordInteraction('Create Estimate button clicked');
        dispatch(showCreateEstimateDialog());
      }}
    />
  );

  const generateEnvelopes = autoLayoutEnabled ? (
    <SceneMenuItem
      icon={<AutoFixHighIcon />}
      text="Auto Layout"
      onClick={async () => {
        monitor.recordInteraction('Generate Envelopes button clicked');
        await dispatch(generateEnvelopesForRoofLayout());
      }}
    />
  ) : null;

  const openOtherProjectItem = (
    <SceneMenuItem
      icon={<ListIcon />}
      text="Open Other Project"
      onClick={() => {
        dispatch(showChooseProjectDialog());
      }}
    />
  );

  if (!canDesign) {
    return (
      <SceneMenuContent>
        {generateEnvelopes}
        {requestQuoteItem}
        {openOtherProjectItem}
      </SceneMenuContent>
    );
  }

  return (
    <SceneMenuFragment>
      <SceneMenuContent>
        {newLayoutModeEnabled && (
          <SceneMenuFragment>
            {isRootUser || canImportCrowdbuildFiles ? (
              <SceneMenuFragment>
                <CascadingSceneMenuItem icon={<ImportIcon />} label="Import Project Data">
                  {isRootUser ? (
                    <SceneMenuItem
                      text="All File Data"
                      onClick={(event) => {
                        event.preventDefault();

                        selectFile()
                          .then(processUploadedFile)
                          .finally(() => dispatch(clearRightClickedItems()));

                        monitor.recordInteraction('Import Project Data => All File Data menu item clicked');
                      }}
                    />
                  ) : null}
                  {isRootUser ? (
                    <SceneMenuItem
                      text="Layout & Hierarchy Only"
                      onClick={(event) => {
                        event.preventDefault();

                        selectFile()
                          .then((file) => processUploadedFile(file, { layoutOnly: true }))
                          .finally(() => dispatch(clearRightClickedItems()));

                        monitor.recordInteraction('Import Project Data => Layout & Hierarchy Only menu item clicked');
                      }}
                    />
                  ) : null}
                  {canImportCrowdbuildFiles ? (
                    <SceneMenuItem
                      text="Stitcher JSON"
                      onClick={(event) => {
                        event.preventDefault();

                        selectFile()
                          .then(processCrowdBuildFile)
                          .finally(() => {
                            dispatch(clearRightClickedItems());
                          });

                        monitor.recordInteraction('Import Project Data => CrowdBuild JSON menu item clicked');
                      }}
                    />
                  ) : null}
                </CascadingSceneMenuItem>

                {isRootUser ? (
                  <CascadingSceneMenuItem icon={<ExportIcon />} label="Export Project Data">
                    <SceneMenuItem
                      text="All Supported Project Data"
                      onClick={async () => {
                        await dispatch(downloadProject());
                        monitor.recordInteraction(
                          'Export Project Data => All Supported Project Data menu item clicked',
                        );
                      }}
                    />
                    <SceneMenuItem
                      text="Layout & Hierarchy Only"
                      onClick={async () => {
                        await dispatch(downloadProject({ layoutOnly: true }));
                        monitor.recordInteraction('Export Project Data => Layout & Hierarchy Only menu item clicked');
                      }}
                    />
                  </CascadingSceneMenuItem>
                ) : null}
              </SceneMenuFragment>
            ) : null}

            <Divider />
          </SceneMenuFragment>
        )}

        <AddItemMenuItem type="componentDesign" parentGuid={props.guid} />
        <ImportComponentDesignMenuItem />

        <Divider />

        {inLayoutMode && !layoutIsViewOnly && (
          <SceneMenuItem
            icon={<DesignIcon />}
            text="Design All Truss Envelopes"
            onClick={async () => {
              await dispatch(designAllTrussEnvelopes());
              monitor.recordInteraction('Design Truss Envelopes button clicked');
            }}
          />
        )}

        <AnalyzeAllChangedMenuItem type="project" modelGuid={props.guid} />

        <Divider />

        {project && (
          <>
            <SceneMenuItem
              text="Edit Properties"
              icon={<EditIcon />}
              onClick={async () => {
                await dispatch(handleStandardSceneItemEditPropertiesAction('project', project));
                monitor.recordInteraction('Project => Edit Properties menu item clicked');
              }}
            />
            <SettingsSubMenu entry={{ type: 'project', item: project }} />
          </>
        )}
        <LockOptionsSubMenu type="project" guid={props.guid} />

        <Divider />
        {generateEnvelopes}
        {requestQuoteItem}
        {openOtherProjectItem}
      </SceneMenuContent>

      {renderFileSelect()}
    </SceneMenuFragment>
  );
}
